import { useSubscription } from "@/helper/useSubscription";
import { useAppSelector } from "@/lib/hooks";
import { Bookmark } from "@/models/bookmark";
import { OPTIONS_SUBSCRIPTION } from "@/models/subscription";
import ReportServices from "@/services/main/reportServices";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ShareIcon from "@mui/icons-material/Share";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
interface BookmarkReviewMobileProps {
  pages: Array<{ id: number; title: string }>;
  selectedPage: number;
  onPageSelect: (pageId: number) => void;
  data: any[];
  isLoading?: boolean;
  reportId: number;
  handleMutationCheckLimitShare: (
    reportId: number | string,
    page: number | null,
    pages: number[] | null
  ) => void;
  handleCalculatePercentage: number;
  sidebarState?: {
    selectedPage: number;
    sidebarVisible: boolean;
  };
  locale: string;
}
const montserrat = Montserrat({
  subsets: ["latin"],
});
function BookmarkReviewMobile({
  pages,
  selectedPage,
  onPageSelect,
  data,
  isLoading,
  reportId,
  handleMutationCheckLimitShare,
  handleCalculatePercentage,
  sidebarState,
  locale,
}: BookmarkReviewMobileProps) {
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { hasPermission } = useSubscription();
  const [openDialogWarning, setOpenDialogWarning] = useState<boolean>(false);
  const { allowShareReport } = useAppSelector((state) => state.report);
  useEffect(() => {
    const handleContextMenu = (event: Event) => {
      event.preventDefault();
    };

    const images = scrollContainerRef.current?.querySelectorAll("img");

    images?.forEach((img) => {
      img.addEventListener("contextmenu", handleContextMenu);
      // Vô hiệu hóa drag and drop
      img.addEventListener("dragstart", handleContextMenu);
      // Vô hiệu hóa select text trên ảnh
      img.style.userSelect = "none";
      img.style.pointerEvents = "auto";
    });

    return () => {
      images?.forEach((img) => {
        img.removeEventListener("contextmenu", handleContextMenu);
        img.removeEventListener("dragstart", handleContextMenu);
      });
    };
  }, [pages]);
  const mutationRemoveBookmark = useMutation({
    mutationKey: ["remove-report-bookmark"],
    mutationFn: async (data: FormData) => {
      return await ReportServices.addReportBookmark(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bookmarks", reportId, locale],
      });
    },
  });
  const mutationChangeNameBookmark = useMutation({
    mutationKey: ["change-name-bookmark"],
    mutationFn: async ({
      bookmarkId,
      data,
    }: {
      bookmarkId: number;
      data: FormData;
    }) => {
      return await ReportServices.changeNameBookmark(bookmarkId, data);
    },
    onSuccess: (res, variables) => {
      const oldData: any[] | undefined = queryClient.getQueryData([
        "bookmarks",
        reportId,
        locale,
      ]);
      if (oldData) {
        const name = variables.data.get("name");
        const newData = oldData.map((item) =>
          item?.id === res?.data?.id ? { ...item, name } : item
        );
        queryClient.setQueryData(["bookmarks", reportId, locale], newData);
      }
      setEditingId(null);
    },
  });

  const handleRemoveBookmark = (bookmark: any) => {
    const formData = new FormData();
    formData.append("reportId", String(reportId));
    formData.append("page", String(bookmark.page));
    formData.append("image", bookmark.image);
    formData.append("language", locale);
    mutationRemoveBookmark.mutate(formData);
  };

  const handleEditSave = (bookmarkId: number) => {
    const formData = new FormData();
    const newValue = inputRef.current?.value || "";
    if (!newValue || !newValue.trim()) {
      return;
    }
    formData.append("name", newValue);
    mutationChangeNameBookmark.mutate({
      bookmarkId: bookmarkId,
      data: formData,
    });
  };
  const handleEditCancel = () => {
    setEditingId(null);
  };
  const getInitialValue = (bookmark: any) => {
    return bookmark.name || `${bookmark.page}`;
  };
  const handleShareBookmark = useCallback(() => {
    const tranferPages = data.map((item) => Number(item.page)) ?? [];
    if (tranferPages?.length >= handleCalculatePercentage) {
      handleClickOpenWarnning();
      return;
    }
    handleMutationCheckLimitShare(reportId, null, tranferPages);
  }, [data]);

  const handleClickOpenWarnning = () => {
    setOpenDialogWarning(true);
  };

  const handleCloseWarning = () => {
    setOpenDialogWarning(false);
  };
  return (
    <Fragment>
      <Dialog
        open={openDialogWarning}
        onClose={handleCloseWarning}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{}}
      >
        <DialogTitle
          id="alert-dialog-title"
          component={"div"}
          className="flex flex-col items-center gap-1 !p-6"
        >
          <Box className="w-full relative h-4">
            <IconButton
              className="!absolute !right-0 !top-1/2 !-translate-y-1/2 rounded-full !bg-cimigo-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                handleCloseWarning();
              }}
              size="small"
            >
              <CloseIcon className="!w-6 !h-6 !text-cimigo-gray-700" />
            </IconButton>
          </Box>
          <Box className="flex items-center justify-start gap-4 w-full">
            <Box className="!bg-[#FBF3DF] flex items-center justify-center p-2">
              <PriorityHighIcon className="!text-orange-dark" />
            </Box>
            <Box className="flex items-start justify-start">
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: "32px",
                  color: "var(--cimigo-gray-1100)",
                }}
                translation-key="bookmark_share_litmit_pages_title_warning"
              >
                {t("bookmark_share_litmit_pages_title_warning")}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent className="!p-6 !pb-8 !pt-0">
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "24px",
              color: "var(--cimigo-gray-1100)",
            }}
            translation-key="bookmark_share_litmit_pages"
          >
            {t("bookmark_share_litmit_pages")}
          </DialogContentText>
        </DialogContent>
      </Dialog>
      <motion.div
        initial={{ height: 0 }}
        animate={{
          height: 400,
          transition: {
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1],
          },
        }}
        exit={{
          height: 0,
          transition: {
            duration: 0.35,
            ease: [0.55, 0, 0.55, 0.2],
          },
        }}
        className="absolute right-0 w-full"
        style={{
          isolation: "isolate",
          overflow: "hidden",
          willChange: "height",
        }}
      >
        <Box className=" p-[16px] h-[400px] !border-r-gray_10 !bg-eerie-black-65 bg-opacity-50  z-[11] overflow-y-auto !hide-scrollbar">
          <Box
            ref={scrollContainerRef}
            className={`grid grid-cols-2 ${sidebarState?.sidebarVisible ? "md:grid-cols-3 " : "md:grid-cols-4"} place-items-center justify-center items-start w-full gap-[16px]`}
          >
            {isLoading ? (
              <Box className={"flex w-full h-full items-center justify-center"}>
                <CircularProgress />
              </Box>
            ) : (
              data?.map((bookmark: Bookmark) => (
                <Card
                  elevation={0}
                  key={bookmark.id}
                  onClick={() => onPageSelect(bookmark.page)}
                  className={`w-[140px] cursor-pointer rounded-none !bg-inherit relative ${
                    selectedPage === bookmark.page
                      ? "border-1 border-blue-500"
                      : "border-2 border-transparent"
                  }`}
                  sx={{
                    borderRadius: 0,
                  }}
                >
                  <IconButton
                    className={`!absolute !top-1 !left-1 !w-6 !h-6 !z-10  ${
                      editingId === bookmark.id
                        ? "!bg-green-500 hover:!bg-green-600"
                        : "!bg-blue-500 hover:!bg-blue-600"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (editingId === bookmark.id) {
                        handleEditCancel();
                      } else {
                        setEditingId(bookmark.id);
                      }
                    }}
                    size="small"
                  >
                    <EditIcon className="!w-4 !h-4 !text-white" />
                  </IconButton>

                  <IconButton
                    className="!absolute !top-1 !right-1 !w-6 !h-6 !bg-red-500 hover:!bg-red-600 !z-10 "
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveBookmark(bookmark);
                    }}
                    size="small"
                  >
                    <CloseIcon className="!w-4 !h-4 !text-white" />
                  </IconButton>

                  <CardMedia
                    component="div"
                    className="relative w-full bg-gray-100 overflow-auto"
                    sx={{
                      border:
                        selectedPage === bookmark.page
                          ? "2px solid #3B82F6"
                          : "2px solid transparent",
                    }}
                  >
                    <Box className="w-full h-full flex items-center justify-center text-gray-400">
                      <Image
                        src={bookmark?.image}
                        alt={t("bookmark_review_page_thumbnail_alt", {
                          number: bookmark.id,
                        })}
                        width={0}
                        height={0}
                        sizes="100vw"
                        style={{
                          width: "100%",
                          height: "auto",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        priority
                      />
                    </Box>
                  </CardMedia>
                  <CardContent className="!py-1 !px-2">
                    {editingId === bookmark.id ? (
                      <TextField
                        inputRef={inputRef}
                        defaultValue={getInitialValue(bookmark)}
                        onBlur={() => handleEditSave(bookmark.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleEditSave(bookmark.id);
                          } else if (e.key === "Escape") {
                            handleEditCancel();
                          }
                        }}
                        autoFocus
                        size="small"
                        sx={{
                          "& .MuiInputBase-input": {
                            color: "white",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            fontSize: "12px",
                            lineHeight: "24px",
                            textAlign: "center",
                            padding: "2px 4px",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "white",
                            },
                            "&:hover fieldset": {
                              borderColor: "white",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "white",
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box
                        className="h-auto"
                        sx={{
                          maxHeight: "50px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        <Typography
                          variant="body2"
                          className="text-center !text-white !font-[Montserrat,sans-serif] !font-[500] !text-[12px] !leading-[24px] !tracking-[0px]"
                        >
                          {bookmark.name
                            ? bookmark.name
                            : t("bookmark_review_page_number", {
                                number: bookmark.page,
                              })}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
            {data?.length === 0 && (
              <Typography
                translation-key="bookmark_review_no_bookmarks"
                className="text-white italic p-4 text-center col-span-2"
              >
                {t("bookmark_review_no_bookmarks")}
              </Typography>
            )}
          </Box>
          {data?.length === 0 ? null : allowShareReport ||
            hasPermission(OPTIONS_SUBSCRIPTION.PREMIUM) ? (
            <Box className="absolute bottom-1 right-1 flex justify-end">
              <Tooltip
                title={
                  <span
                    translation-key="report_content_add_share_bookmark"
                    className={montserrat.className}
                  >
                    {t("report_content_add_share_bookmark")}
                  </span>
                }
                disableInteractive
                placement="bottom"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  className="!bg-white !p-1 !rounded h-[32px] md:h-[32px] max-h-[32px] md:max-h-[32px] cursor-pointer hover:bg-white bg-gray_2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShareBookmark();
                  }}
                >
                  <ShareIcon className="w-5 h-5 md:w-6 md:h-6 text-gray-80" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
      </motion.div>
    </Fragment>
  );
}

export default BookmarkReviewMobile;
